import {
    Box,
    Divider,
    Flex,
    Heading,
    Image,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { SafeHTML } from "components/safe-html";
import ActivityBadges from "components/selection-components/select-activity/activity-badges/activity-badges";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { urlBuilder } from "search-params/url-builder";
import { AvailableTimesSection } from "types/available-times";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";

interface IActivityItem {
    section: AvailableTimesSection;
    onClick: () => void;
    unavailable?: boolean;
}

const ActivityItem = ({ section, onClick, unavailable }: IActivityItem) => {
    const { t } = useTranslation();
    const [showImage, setShowImage] = useState(true);

    const imageParams: Record<string, string | undefined> = {
        AspectRatio: ImageServiceAspectRatio.OneByOne,
        Type: "webp",
    };
    const baseUrl = `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${section.image}`;
    const sectionImageUrlOneByOne = urlBuilder(baseUrl, imageParams);
    return (
        <Box
            as="button"
            w="full"
            onClick={onClick}
            type="button"
            aria-label={t("viewDetailsOfXActivity", { activity: section.name })}
            // @ts-expect-error // ! TODO: Include _focuse for box in casper-ui-library
            _focus={{ boxShadow: "var(--theme-shadows-outline)" }}
            _hover={{ bg: ThemeColorVariable.AccentHoverAlpha }}
        >
            <Flex
                justify="space-between"
                py={ThemeSpaceVariable.Medium}
                align="center"
                gap={ThemeSpaceVariable.Small}
                flex={1}
                px={{ md: ThemeSpaceVariable.Small }}
            >
                <Stack spacing={ThemeSpaceVariable.None}>
                    <Flex align="center" gap={ThemeSpaceVariable.Small}>
                        <Heading
                            as="h3"
                            noOfLines={1}
                            fontFamily={ThemeFontVariable.SubHeader}
                            fontWeight="medium"
                            color={ThemeColorVariable.OnBackgroundHeader}
                            fontSize={{
                                base: ThemeFontSizeVariable.Large,
                                md: ThemeFontSizeVariable.XLarge,
                            }}
                        >
                            {section.name}
                        </Heading>
                        {unavailable && (
                            <Text
                                key={`limited-${section.id || section.name}-${unavailable}`}
                                height={{
                                    base: ThemeFontSizeVariable.XLarge,
                                    md: "unset",
                                }}
                                noOfLines={1}
                                whiteSpace="break-spaces"
                                fontSize={{
                                    base: ThemeFontSizeVariable.Small,
                                    md: ThemeFontSizeVariable.Medium,
                                }}
                                color={ThemeColorVariable.OnBackgroundSubdued}
                                maxW="380px"
                            >
                                {t("limitedAvailability")}
                            </Text>
                        )}
                    </Flex>
                    <Text
                        height={{
                            base: ThemeFontSizeVariable.XLarge,
                            md: "unset",
                        }}
                        noOfLines={1}
                        whiteSpace="break-spaces"
                        fontSize={{
                            base: ThemeFontSizeVariable.Small,
                            md: ThemeFontSizeVariable.Large,
                        }}
                        maxW="380px"
                    >
                        <SafeHTML
                            element="span"
                            options={{
                                allowedTags: ["a", "br"],
                                allowedSchemes: ["tel", "mailto", "https"],
                            }}
                            html={section.description}
                        />
                    </Text>
                    <ActivityBadges section={section} />
                </Stack>
                {section.image && showImage && (
                    <Image
                        src={sectionImageUrlOneByOne}
                        maxH="72px"
                        borderRadius={ThemeSpaceVariable.Small}
                        onError={() => setShowImage(false)}
                    />
                )}
            </Flex>
            <Divider />
        </Box>
    );
};

export default ActivityItem;
